import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import socket, { ensureSocketReady } from '../socket';
import { useAuth } from '../context/AuthContext';
import Button from './ui/Button';
import Input from './ui/Input';
import VotingCard from './ui/VotingCard';
import Card from './ui/Card';
import { clampUserName } from '../constants/limits';

function PokerRoom() {
    const { logout } = useAuth();
    const { roomId: paramRoomId } = useParams();

    const [roomId, setRoomId] = useState(paramRoomId);
    const [userName, setUserName] = useState('');
    const [users, setUsers] = useState([]); // [{name, role, hasVoted}]
    const [selectedCard, setSelectedCard] = useState(null);
    const [canReveal, setCanReveal] = useState(false);
    const [votes, setVotes] = useState([]); // [{user, role, vote}]
    const [average, setAverage] = useState(null);
    const [averagesByRole, setAveragesByRole] = useState(null); // {DEV, QA, DEVOPS, PO, LEADER, OUTROS, ...}
    const [cards, setCards] = useState([]);
    const [roomName, setRoomName] = useState('');
    const [stories, setStories] = useState([]);
    const [storyInput, setStoryInput] = useState('');
    const [activeStoryId, setActiveStoryId] = useState(null);
    const [infoMessage, setInfoMessage] = useState('');
    const navigate = useNavigate();
    const joinInFlightRef = useRef(false);

    const showInfoMessage = (message) => {
        setInfoMessage(message);
        setTimeout(() => setInfoMessage(''), 6000);
    };

    // Listeners primeiro; depois join único (ACK). Rejoin em reconnect.
    useEffect(() => {
        let cancelled = false;

        const onUpdateUsers = ({ users: nextUsers }) => setUsers(nextUsers);
        const onAllVoted = () => setCanReveal(true);
        const onVotesRevealed = ({ votes: nextVotes, average: avg, averagesByRole: byRole, revealedBy }) => {
            setVotes(nextVotes);
            setAverage(avg);
            setAveragesByRole(byRole || null);
            if (revealedBy) showInfoMessage(`${revealedBy} revelou os votos`);
        };
        const onVotesReset = (payload) => {
            setVotes([]);
            setSelectedCard(null);
            setCanReveal(false);
            setAverage('?');
            setAveragesByRole(null);
            if (payload?.resetBy) showInfoMessage(`${payload.resetBy} resetou os votos`);
        };
        const onSetSequence = ({ sequence }) => setCards(sequence);
        const onRoomInfo = ({ roomName: name }) => setRoomName(name);
        const onRoomData = ({
            roomName: name,
            cardOptions,
            users: nextUsers,
            votes: nextVotes,
            votingOpen,
            stories: nextStories,
            activeStoryId: nextActiveStoryId,
        }) => {
            setRoomName(name);
            setCards(cardOptions || []);
            setUsers(nextUsers || []);
            setStories(nextStories || []);
            setActiveStoryId(nextActiveStoryId || null);
            if (!votingOpen) {
                setVotes(nextVotes || []);
                setCanReveal(false);
                setAverage('?');
                setAveragesByRole(null);
            }
        };
        const onStoryAdded = ({ stories: nextStories, activeStoryId: nextActiveStoryId }) => {
            setStories(nextStories);
            setActiveStoryId(nextActiveStoryId);
        };
        const onRemoved = () => {
            alert('Você foi removido da sala.');
            localStorage.removeItem('roomId');
            localStorage.removeItem('userName');
            navigate('/');
        };

        socket.on('updateUsers', onUpdateUsers);
        socket.on('allVoted', onAllVoted);
        socket.on('votesRevealed', onVotesRevealed);
        socket.on('votesReset', onVotesReset);
        socket.on('setSequence', onSetSequence);
        socket.on('roomInfo', onRoomInfo);
        socket.on('roomData', onRoomData);
        socket.on('storyAdded', onStoryAdded);
        socket.on('removed', onRemoved);

        const onAuthError = ({ message }) => {
            alert(message || 'Sessão expirada. Faça login novamente.');
            logout();
            navigate('/');
        };
        socket.on('authError', onAuthError);

        const performJoin = async (isReconnect = false) => {
            if (joinInFlightRef.current) return;

            const savedRoomId = localStorage.getItem('roomId');
            const savedUserName = clampUserName(localStorage.getItem('userName'));
            const savedUserRole = (localStorage.getItem('userRole') || 'OUTROS').toUpperCase();

            if (!savedRoomId || !savedUserName) {
                if (!cancelled) navigate('/');
                return;
            }

            joinInFlightRef.current = true;

            try {
                await ensureSocketReady();
                if (cancelled) return;

                socket.emit('checkRoomExists', savedRoomId, ({ exists }) => {
                    if (cancelled) return;

                    if (!exists) {
                        localStorage.removeItem('roomId');
                        localStorage.removeItem('userName');
                        localStorage.removeItem('userRole');
                        navigate('/');
                        joinInFlightRef.current = false;
                        return;
                    }

                    socket.emit(
                        'joinRoom',
                        { roomId: savedRoomId, userName: savedUserName, role: savedUserRole },
                        (ack) => {
                            joinInFlightRef.current = false;
                            if (cancelled) return;

                            if (ack?.ok) {
                                if (ack.users) setUsers(ack.users);
                                setRoomId(savedRoomId);
                                setUserName(savedUserName);
                            } else if (ack?.error === 'unauthorized') {
                                logout();
                                navigate('/');
                            } else if (!isReconnect) {
                                navigate('/');
                            }
                        }
                    );
                });
            } catch {
                joinInFlightRef.current = false;
                if (!cancelled && !isReconnect) navigate('/');
            }
        };

        const onDisconnect = () => {
            joinInFlightRef.current = false;
        };

        const onReconnect = () => {
            if (localStorage.getItem('roomId') && localStorage.getItem('userName')) {
                performJoin(true);
            }
        };

        socket.on('disconnect', onDisconnect);
        socket.on('connect', onReconnect);
        performJoin(false);

        return () => {
            cancelled = true;
            joinInFlightRef.current = false;
            socket.off('disconnect', onDisconnect);
            socket.off('connect', onReconnect);
            socket.off('updateUsers', onUpdateUsers);
            socket.off('allVoted', onAllVoted);
            socket.off('votesRevealed', onVotesRevealed);
            socket.off('votesReset', onVotesReset);
            socket.off('setSequence', onSetSequence);
            socket.off('roomInfo', onRoomInfo);
            socket.off('roomData', onRoomData);
            socket.off('storyAdded', onStoryAdded);
            socket.off('removed', onRemoved);
            socket.off('authError', onAuthError);
        };
    }, [navigate, paramRoomId]);

    const handleVote = (value) => {
        setSelectedCard(value);
        socket.emit('vote', { roomId, vote: value });
    };

    const handleRevealVotes = () => socket.emit('revealVotes', { roomId, userName });
    const handleResetVotes = () => socket.emit('resetVotes', { roomId, userName });

    const voltarHome = () => {
        localStorage.removeItem('roomId');
        localStorage.removeItem('userName');
        navigate('/');
    };

    const handleRemoveUser = (targetName) => {
        if (window.confirm(`Deseja remover ${targetName} da sala?`)) {
            socket.emit('removeUser', { roomId, userName: targetName });
        }
    };

    const handleAddStory = () => {
        const s = storyInput.trim();
        if (!s) {
            alert('Informe alguma história para cadastrar!');
            return;
        }
        socket.emit('addStory', { roomId, storyName: s });
        setStoryInput('');
    };

    // ======== helpers para exibição das médias por papel ========
    const renderRoleAverages = () => {
        if (!averagesByRole) return null;

        // Ordem preferida e labels bonitos
        const preferred = ['DEV', 'QA', 'DEVOPS', 'PO', 'LEADER', 'OUTROS'];
        const labels = {
            DEV: 'Dev',
            QA: 'QA',
            DEVOPS: 'DevOps',
            PO: 'PO',
            LEADER: 'Leader',
            OUTROS: 'Outros',
        };

        const presentKeys = Object.keys(averagesByRole || {});
        const extras = presentKeys.filter((k) => !preferred.includes(k)).sort((a, b) => a.localeCompare(b));
        const displayOrder = [...preferred, ...extras].filter((k) => averagesByRole[k] !== undefined);

        return (
            <div className="mt-4">
                <strong className="text-slate-200">Médias por papel:</strong>
                <ul className="list-none p-0 m-0 mt-2 space-y-2">
                    {displayOrder.map((roleKey) => (
                        <li
                            key={roleKey}
                            className="flex justify-between items-center bg-surface-hover px-3 py-2 rounded-lg"
                        >
                            <span className="font-semibold text-slate-200">{labels[roleKey] || roleKey}</span>
                            <span className="text-primary-light font-bold">{averagesByRole[roleKey]}</span>
                        </li>
                    ))}
                </ul>
            </div>
        );
    };
    // ============================================================

    // Mapa com menor e maior voto por papel (role)
    const roleExtremes = (() => {
        if (!votes || votes.length === 0) return {};

        const map = {};

        votes.forEach(({ role, vote }) => {
            const roleKey = (role || 'OUTROS').toUpperCase();
            const numericVote = Number(vote);

            if (Number.isNaN(numericVote)) return;

            if (!map[roleKey]) {
                map[roleKey] = { min: numericVote, max: numericVote };
            } else {
                if (numericVote < map[roleKey].min) map[roleKey].min = numericVote;
                if (numericVote > map[roleKey].max) map[roleKey].max = numericVote;
            }
        });

        return map;
    })();

    // Apenas exibição: agrupa por cargo (mesma ordem das médias) e, no grupo, por voto crescente
    const votesDisplayOrder = useMemo(() => {
        if (!votes || votes.length === 0) return [];
        const preferred = ['DEV', 'QA', 'DEVOPS', 'PO', 'LEADER', 'OUTROS'];
        const present = [...new Set(votes.map((v) => (v.role || 'OUTROS').toUpperCase()))];
        const extraRoles = present.filter((k) => !preferred.includes(k)).sort((a, b) => a.localeCompare(b, 'pt-BR'));
        const roleOrder = [...preferred.filter((k) => present.includes(k)), ...extraRoles];
        const rank = (r) => {
            const i = roleOrder.indexOf(r);
            return i === -1 ? roleOrder.length : i;
        };
        return [...votes].sort((a, b) => {
            const ra = (a.role || 'OUTROS').toUpperCase();
            const rb = (b.role || 'OUTROS').toUpperCase();
            const cr = rank(ra) - rank(rb);
            if (cr !== 0) return cr;
            const na = Number(a.vote);
            const nb = Number(b.vote);
            if (!Number.isNaN(na) && !Number.isNaN(nb) && na !== nb) return na - nb;
            if (Number.isNaN(na) !== Number.isNaN(nb)) return Number.isNaN(na) ? 1 : -1;
            return (a.user || '').localeCompare(b.user || '', 'pt-BR');
        });
    }, [votes]);

    return (
        <div className="flex flex-col lg:flex-row gap-6 w-full max-w-7xl mx-auto p-4 lg:p-6">
            {/* SIDEBAR */}
            <aside className="w-full lg:w-80 shrink-0 min-w-0">
                <Card className="p-6 sticky top-6 overflow-hidden">
                    <div className="mb-6 min-w-0">
                        <h3 className="text-sm font-medium text-slate-400 mb-1">Sala</h3>
                        <p className="text-xl font-bold text-slate-100">{roomId}</p>
                        <p className="text-sm text-slate-300 mt-1 flex gap-1 min-w-0">
                            <span className="shrink-0">Dono:</span>
                            <span
                                className="text-primary-light font-semibold truncate min-w-0"
                                title={roomName}
                            >
                                {roomName}
                            </span>
                        </p>
                    </div>

                    <div className="mb-6 min-w-0">
                        <h3 className="text-sm font-medium text-slate-400 mb-3">👥 Participantes</h3>
                        <ul className="space-y-2">
                            {users.map((user, i) => (
                                <li key={i} className="flex justify-between items-center gap-2 text-sm bg-surface-hover px-3 py-2 rounded-lg min-w-0">
                                    <span className="flex items-center gap-1 min-w-0 flex-1 text-slate-200">
                                        <span className="truncate min-w-0" title={user.name}>
                                            {user.name}
                                        </span>
                                        <span className="shrink-0 whitespace-nowrap">
                                            {' '}- {user.role}
                                            {user.hasVoted && ' ✔️'}
                                        </span>
                                    </span>
                                    {userName === roomName && user.name !== userName && (
                                        <span
                                            onClick={() => handleRemoveUser(user.name)}
                                            className="text-red-400 hover:text-red-300 cursor-pointer transition-colors shrink-0"
                                        >
                                            ❌
                                        </span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="mb-6">
                        <h3 className="text-sm font-medium text-slate-400 mb-3">📝 Nova História</h3>
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={storyInput}
                                onChange={(e) => setStoryInput(e.target.value)}
                                placeholder="Nome da história"
                                className="flex-1 px-3 py-2 text-sm rounded-lg bg-surface border border-white/10 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/50"
                                maxLength={32}
                            />
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={handleAddStory}
                            >
                                +
                            </Button>
                        </div>
                    </div>

                    <div>
                        <h3 className="text-sm font-medium text-slate-400 mb-3">📋 Histórias</h3>
                        <ul className="space-y-2">
                            {stories.map((story, idx) => (
                                <li
                                    key={idx}
                                    className="bg-surface-hover px-3 py-2 rounded-lg"
                                >
                                    <div className="flex justify-between items-start gap-2">
                                        <span className="text-sm text-slate-200 flex-1">
                                            {story.name}
                                            {story.revealed && <span className="text-primary-light ml-2">({story.average})</span>}
                                            {story.id === activeStoryId && <span className="text-accent ml-2">(Ativa)</span>}
                                        </span>
                                        <div className="flex gap-1">
                                            {story.id !== activeStoryId && (
                                                <>
                                                    <button
                                                        className="text-xs px-2 py-1 bg-primary hover:bg-primary-hover text-white rounded transition-colors"
                                                        onClick={() => socket.emit('setActiveStory', { roomId, storyId: story.id })}
                                                    >
                                                        Votar
                                                    </button>
                                                    {userName === roomName && (
                                                        <button
                                                            className="text-xs px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
                                                            onClick={() => socket.emit('deleteStory', { roomId, storyId: story.id })}
                                                        >
                                                            🗑️
                                                        </button>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </Card>
            </aside>

            {/* MAIN CONTENT */}
            <main className="flex-1 min-w-0">
                <Card className="p-6 lg:p-8">
                    {infoMessage && (
                        <div className="mb-6 bg-info/10 border border-info/30 rounded-lg px-4 py-3 text-info font-medium">
                            {infoMessage}
                        </div>
                    )}

                    {votes.length === 0 ? (
                        <>
                            <h3 className="text-xl font-semibold text-slate-100 mb-6 text-center">
                                🃏 Escolha sua carta
                            </h3>
                            <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-3 mb-6">
                                {cards.map((value, idx) => (
                                    <VotingCard
                                        key={idx}
                                        value={value}
                                        selected={selectedCard === value}
                                        onClick={() => handleVote(value)}
                                    />
                                ))}
                            </div>
                            {canReveal && (
                                <div className="text-center">
                                    <Button
                                        variant="secondary"
                                        size="lg"
                                        onClick={handleRevealVotes}
                                    >
                                        👁️ Revelar Votos
                                    </Button>
                                </div>
                            )}
                        </>
                    ) : (
                        <div>
                            <h3 className="text-xl font-semibold text-slate-100 mb-4">📊 Resultados</h3>
                            <ul className="space-y-2 mb-6">
                                {votesDisplayOrder.map((vote, idx) => {
                                    const roleKey = (vote.role || 'OUTROS').toUpperCase();
                                    const numericVote = Number(vote.vote);
                                    const extremes = roleExtremes[roleKey];

                                    const isNumeric = !Number.isNaN(numericVote);
                                    const isMin = isNumeric && extremes && numericVote === extremes.min;
                                    const isMax = isNumeric && extremes && numericVote === extremes.max;

                                    const liClasses = [
                                        'flex justify-between items-center gap-2 bg-surface-hover px-4 py-3 rounded-lg min-w-0',
                                        isMax && 'border border-amber-400/50 bg-amber-500/5',
                                        !isMax && isMin && 'border border-emerald-400/50 bg-emerald-500/5',
                                    ]
                                        .filter(Boolean)
                                        .join(' ');

                                    return (
                                        <li key={`${vote.user}-${vote.role}-${idx}`} className={liClasses}>
                                            <span className="flex items-center gap-1 min-w-0 flex-1">
                                                <span className="truncate min-w-0 text-slate-200" title={vote.user}>
                                                    {vote.user}
                                                </span>
                                                <span className="shrink-0 text-slate-400 whitespace-nowrap">
                                                    ({vote.role})
                                                </span>
                                                {isMin && (
                                                    <span className="shrink-0 ml-1 inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold border border-emerald-400/60 text-emerald-300 bg-emerald-500/10">
                                                        Menor
                                                    </span>
                                                )}
                                                {isMax && (
                                                    <span className="shrink-0 ml-1 inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold border border-amber-400/60 text-amber-200 bg-amber-500/10">
                                                        Maior voto
                                                    </span>
                                                )}
                                            </span>
                                            <span className="text-primary-light font-bold text-lg shrink-0">{vote.vote}</span>
                                        </li>
                                    );
                                })}
                            </ul>

                            <div className="bg-surface-elevated p-4 rounded-lg mb-4">
                                <div className="text-center">
                                    <p className="text-sm text-slate-400 mb-1">Média Geral</p>
                                    <p className="text-4xl font-bold text-secondary">{average}</p>
                                </div>
                            </div>

                            {/* Médias por papel dinâmicas */}
                            {renderRoleAverages()}
                        </div>
                    )}

                    <div className="flex flex-col sm:flex-row gap-3 mt-8 pt-6 border-t border-white/10">
                        <Button
                            variant="danger"
                            onClick={handleResetVotes}
                            className="flex-1"
                        >
                            🔄 Resetar Votos
                        </Button>
                        <Button
                            variant="ghost"
                            onClick={voltarHome}
                            className="flex-1"
                        >
                            🚪 Sair da Sala
                        </Button>
                    </div>
                </Card>
            </main>
        </div>
    );
}

export default PokerRoom;
